import React, { useState, useEffect } from 'react';
import { DollarSign, Search, Download, RefreshCw, AlertTriangle, X, CheckCircle, List, Check } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Button,
  Checkbox
} from '@mui/material';
import MainService from '../../services/MainService';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';

const PaymentApprovals = () => {
  const { language } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState(new Set());

  // Summary totals for selected payments
  const [summaryTotals, setSummaryTotals] = useState({
    TotalClientMoney: 0
  });

  useEffect(() => {
    loadPayments();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [searchTerm, payments]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    calculateTotals();
  }, [selectedPayments, filteredPayments]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.ClientPaymentsForApprovals();
      if (response && Array.isArray(response)) {
        const paymentsWithChecked = response.map(p => ({ ...p, checked: false }));
        setPayments(paymentsWithChecked);
        setFilteredPayments(paymentsWithChecked);
      } else {
        setPayments([]);
        setFilteredPayments([]);
      }
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Failed to load payments');
      setPayments([]);
      setFilteredPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredPayments(payments);
      setCurrentPage(1);
      return;
    }

    const keyword = searchTerm.toLowerCase();
    const filtered = payments.filter(payment => 
      (payment.RefNo && payment.RefNo.toLowerCase().includes(keyword)) ||
      (payment.Employee?.Name && payment.Employee.Name.toLowerCase().includes(keyword)) ||
      (payment.Createdby?.UserName && payment.Createdby.UserName.toLowerCase().includes(keyword)) ||
      (payment.Status && payment.Status.toLowerCase().includes(keyword))
    );
    setFilteredPayments(filtered);
    setCurrentPage(1);
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allIds = new Set(paginatedPayments.map(p => p.Id));
      setSelectedPayments(allIds);
      setFilteredPayments(prev => prev.map(p => 
        paginatedPayments.some(pp => pp.Id === p.Id) ? { ...p, checked: true } : p
      ));
    } else {
      const pageIds = new Set(paginatedPayments.map(p => p.Id));
      setSelectedPayments(prev => {
        const newSet = new Set(prev);
        pageIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      setFilteredPayments(prev => prev.map(p => 
        paginatedPayments.some(pp => pp.Id === p.Id) ? { ...p, checked: false } : p
      ));
    }
  };

  const handleSelectPayment = (paymentId, checked) => {
    setSelectedPayments(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(paymentId);
      } else {
        newSet.delete(paymentId);
        setSelectAll(false);
      }
      return newSet;
    });

    setFilteredPayments(prev => prev.map(p => 
      p.Id === paymentId ? { ...p, checked } : p
    ));
  };

  const calculateTotals = () => {
    const selected = filteredPayments.filter(p => selectedPayments.has(p.Id));
    
    const totals = {
      TotalClientMoney: 0
    };

    selected.forEach(payment => {
      totals.TotalClientMoney += parseFloat(payment.ClientMoney || 0);
    });

    setSummaryTotals(totals);
  };

  const handleApprovePayments = async () => {
    if (selectedPayments.size === 0) {
      setError('Please select at least one payment to approve');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const paymentArray = Array.from(selectedPayments).map(id => ({ Id: id }));
      const response = await MainService.ApproveClientPayment(paymentArray);
      
      if (response === 'Success' || response === true) {
        setSuccess(`Successfully approved ${selectedPayments.size} payment(s)`);
        setSelectedPayments(new Set());
        setSelectAll(false);
        loadPayments();
      } else {
        setError(response || 'Failed to approve payments');
      }
    } catch (err) {
      console.error('Error approving payments:', err);
      setError('Failed to approve payments');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const exportToExcel = () => {
    if (filteredPayments.length === 0) {
      setError('No data to export');
      return;
    }

    const dataToExport = filteredPayments.map(payment => ({
      'Ref No': payment.RefNo || '',
      'Date': formatDate(payment.Date),
      'Client Money': formatCurrency(payment.ClientMoney),
      'Status': payment.Status || '',
      'Employee': payment.Employee?.Name || '',
      'Created By': payment.Createdby?.UserName || '',
      'Clients': payment.ClientsNames?.join(', ') || ''
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payments');
    XLSX.writeFile(wb, `PaymentApprovals_${new Date().toISOString().split('T')[0]}.xlsx`);
    setSuccess('Data exported to Excel successfully');
  };

  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const totalPages = Math.ceil(filteredPayments.length / pageSize);

  // Calculate footer totals for all displayed payments
  const footerTotals = {
    TotalClientMoney: filteredPayments.reduce((sum, p) => sum + parseFloat(p.ClientMoney || 0), 0)
  };

  useEffect(() => {
    if (paginatedPayments.length > 0) {
      const allSelected = paginatedPayments.every(p => selectedPayments.has(p.Id));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedPayments, paginatedPayments]);

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center">
            <List className="w-6 h-6 text-[#46955A] mr-2" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{getPageHeading(language, 'paymentApprovalsList')}</h1>
              <p className="text-sm text-gray-500 mt-1">Review and approve pending client payments</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <TextField
              size="small"
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="w-4 h-4 text-gray-400" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '300px',
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#46955A',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#46955A',
                  },
                },
              }}
            />
            <Tooltip title="Export to Excel">
              <IconButton
                onClick={exportToExcel}
                disabled={filteredPayments.length === 0}
                sx={{
                  color: '#46955A',
                  '&:hover': { backgroundColor: '#f0f9f4' },
                  '&:disabled': { color: '#9CA3AF' }
                }}
              >
                <Download className="w-5 h-5" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                onClick={loadPayments}
                disabled={loading}
                sx={{
                  color: '#46955A',
                  '&:hover': { backgroundColor: '#f0f9f4' }
                }}
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={32} sx={{ color: '#46955A' }} />
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Loading payments...</span>
            </Box>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '8px' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px', width: '40px' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px', width: '40px' }}>
                      <Checkbox
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        size="small"
                        sx={{
                          color: '#46955A',
                          '&.Mui-checked': {
                            color: '#46955A',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Ref No</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Client Money</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#374151', padding: '12px' }}>Created By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedPayments.length > 0 ? (
                    paginatedPayments.map((payment, index) => (
                      <TableRow
                        key={payment.Id}
                        hover
                        sx={{
                          '&:hover': { backgroundColor: '#F9FAFB' },
                          backgroundColor: selectedPayments.has(payment.Id) ? '#f0f9f4' : 'transparent'
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.875rem', color: '#6B7280', padding: '12px' }}>
                          {(currentPage - 1) * pageSize + index + 1}
                        </TableCell>
                        <TableCell sx={{ padding: '12px' }}>
                          <Checkbox
                            checked={selectedPayments.has(payment.Id)}
                            onChange={(e) => handleSelectPayment(payment.Id, e.target.checked)}
                            size="small"
                            sx={{
                              color: '#46955A',
                              '&.Mui-checked': {
                                color: '#46955A',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827', padding: '12px' }}>
                          {payment.RefNo || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          {formatDate(payment.Date)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#059669', padding: '12px' }}>
                          {formatCurrency(payment.ClientMoney)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          <span className="px-2 py-1 text-xs font-medium rounded" style={{ 
                            backgroundColor: payment.Status === 'STATUS DONE' ? '#D1FAE5' : '#FEE2E2',
                            color: payment.Status === 'STATUS DONE' ? '#065F46' : '#991B1B'
                          }}>
                            {payment.Status || 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          {payment.Employee?.Name || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '12px' }}>
                          {payment.Createdby?.UserName || 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ padding: '40px' }}>
                        <div className="flex flex-col items-center justify-center text-gray-400">
                          <DollarSign className="w-12 h-12 mb-3 opacity-50" />
                          <p className="text-sm font-medium">
                            {searchTerm ? 'No payments found matching your search' : 'No payments pending approval'}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {/* Footer Row with Totals */}
                  {paginatedPayments.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#F9FAFB', borderTop: '2px solid #E5E7EB' }}>
                      <TableCell colSpan={4} align="right" sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827', padding: '12px' }}>
                        <strong>Total:</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827', padding: '12px' }}>
                        {formatCurrency(footerTotals.TotalClientMoney)}
                      </TableCell>
                      <TableCell colSpan={3}></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary Totals for All Payments */}
            {filteredPayments.length > 0 && (
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-700 mb-2">All Payments Summary</p>
                  <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-600">
                    <span><strong>Total Client Money:</strong> {formatCurrency(footerTotals.TotalClientMoney)}</span>
                    <span>|</span>
                    <span><strong>Total Payments:</strong> {filteredPayments.length}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Summary Totals for Selected Payments */}
            {selectedPayments.size > 0 && (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Selected Payments Summary</p>
                  <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-600">
                    <span><strong>Total Client Money:</strong> {formatCurrency(summaryTotals.TotalClientMoney)}</span>
                    <span>|</span>
                    <span><strong>Selected Count:</strong> {selectedPayments.size}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-3 mt-4">
              <Button
                variant="outlined"
                startIcon={<RefreshCw className="w-4 h-4" />}
                onClick={loadPayments}
                disabled={loading}
                sx={{
                  borderColor: '#46955A',
                  color: '#46955A',
                  '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' },
                  '&:disabled': { borderColor: '#9CA3AF', color: '#9CA3AF' }
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Check className="w-4 h-4" />}
                onClick={handleApprovePayments}
                disabled={loading || selectedPayments.size === 0}
                sx={{
                  backgroundColor: '#46955A',
                  '&:hover': { backgroundColor: '#3d7d4f' },
                  '&:disabled': { backgroundColor: '#9CA3AF' }
                }}
              >
                Approve Payments ({selectedPayments.size})
              </Button>
            </div>

            {/* Pagination Footer */}
            {filteredPayments.length > 0 && (
              <div className="flex items-center justify-between mt-4 px-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredPayments.length)} of {filteredPayments.length} payments
                  </span>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(e.target.value);
                        setCurrentPage(1);
                      }}
                      sx={{
                        fontSize: '0.875rem',
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#46955A',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#46955A',
                          },
                        },
                      }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, value) => setCurrentPage(value)}
                  color="primary"
                  sx={{
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: '#46955A',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#3d7d4f',
                      },
                    },
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentApprovals;
